import {
  HEADER_ICON,
  HEADER_PRIMARY_BUTTON,
  HEADER_SECONDARY_BUTTON,
  HEADER_SECONDARY_ICON,
} from "../components/layout/headerActionStyles";
import {
  Award,
  Building2,
  Coins,
  Edit,
  FileText,
  Image,
  Library,
  MessageCircle,
  Plus,
  Search,
  UserCheck,
  Users,
  Video,
  X,
  Zap,
} from "lucide-react";
import React, { useEffect, useState, useMemo, useDeferredValue } from "react";
import {
  AdminCompaniesSkeleton,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  PageLayout,
  Select,
} from "../components";
import Pagination from "../components/ui/Pagination";
import { showToastError, showToastSuccess } from "../shared/alertService";
import apiService from "../shared/apiService";
import { useTranslation } from "../contexts/LanguageContext";

interface GlobalCompany {
  id: number;
  name: string;
  cnpj?: string;
  clients_count: number;
  users_count: number;
  posts_created: number;
  posts_published: number;
  images_created: number;
  videos_created: number;
  campaign_leads: number;
  content_library_count: number;
  client_companies: {
    id: number;
    name: string;
    created_at: string;
    credits_balance?: number;
    credits_consumed?: number;
    billing_context?: string;
  }[];
  is_enterprise?: boolean;
  credits_balance?: number;
  credits_consumed?: number;
  faturamento?: number;
  created_at: string;
  updated_at: string;
}

const AdminCompanies: React.FC = () => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState<GlobalCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<GlobalCompany | null>(
    null,
  );
  const [formData, setFormData] = useState({
    name: "",
    organization_id: "",
    is_enterprise: false,
  });
  // product vazio = créditos de IA; "whatsapp"/"email" = envios (entram como pacote, não expiram).
  const [creditsFormData, setCreditsFormData] = useState<{
    amount: string;
    client_company_id: string;
    description: string;
    product: "" | "whatsapp" | "email";
  }>({
    amount: "",
    client_company_id: "",
    description: "",
    product: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [grantingCredits, setGrantingCredits] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [planFilter, setPlanFilter] = useState<"" | "enterprise" | "standard">(
    "",
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);

  const [freeTierConfig, setFreeTierConfig] = useState({
    default_credits: 100,
    trial_days: 15,
    restrict_after_trial: true,
  });
  const [savingConfig, setSavingConfig] = useState(false);

  // Preço do plano recorrente do Virtus Chat — valores digitados em R$ (não em centavos) pra
  // facilitar a edição; convertidos pra centavos só na hora de salvar (mesma unidade usada pelo
  // backend em WhatsappCrmPlanConfig).
  const [whatsappCrmPlanConfig, setWhatsappCrmPlanConfig] = useState({
    base_price: "197.00",
    included_seats: 3,
    extra_seat_price: "0.00",
    // Franquia mensal de envios inclusa na assinatura — redefinida a cada pagamento (não acumula).
    included_whatsapp_sends: 0,
    included_email_sends: 0,
  });
  const [savingWhatsappCrmPlanConfig, setSavingWhatsappCrmPlanConfig] =
    useState(false);

  // Tabela de créditos: global (substitui o dict fixo CREDIT_COSTS) + overrides opcionais por
  // organização ou por Empresa Cliente específica (prioridade: Cliente > Organização > global).
  const [showCreditCostsModal, setShowCreditCostsModal] = useState(false);
  const [creditCostScope, setCreditCostScope] = useState<
    "global" | "organization" | "client"
  >("global");
  const [creditCosts, setCreditCosts] = useState<
    { usage_type: string; cost_credits: number }[]
  >([]);
  const [loadingCreditCosts, setLoadingCreditCosts] = useState(false);
  const [savingCreditCostUsageType, setSavingCreditCostUsageType] = useState<
    string | null
  >(null);
  const [editingCreditCosts, setEditingCreditCosts] = useState<
    Record<string, string>
  >({});

  const [creditOverrideOrgId, setCreditOverrideOrgId] = useState("");
  const [creditOverrideClientId, setCreditOverrideClientId] = useState("");
  const [creditOverrides, setCreditOverrides] = useState<
    { usage_type: string; cost_credits: number }[]
  >([]);
  const [loadingCreditOverrides, setLoadingCreditOverrides] = useState(false);
  const [newOverrideForm, setNewOverrideForm] = useState({
    usage_type: "",
    cost_credits: "",
  });
  const [savingOverride, setSavingOverride] = useState(false);
  const [deletingOverrideUsageType, setDeletingOverrideUsageType] = useState<
    string | null
  >(null);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const getCompanyColor = (name: string) => {
    const colors = [
      "from-purple-500 to-indigo-600",
      "from-blue-500 to-cyan-600",
      "from-emerald-500 to-teal-600",
      "from-rose-500 to-pink-600",
      "from-amber-500 to-orange-600",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const data = await apiService.get("/api/admin/companies");
      setCompanies(data.companies || []);
    } catch (error: any) {
      showToastError(error.message || t("Erro ao carregar organizações"));
    } finally {
      setLoading(false);
    }
  };

  const loadFreeTierConfig = async () => {
    try {
      const data = await apiService.get("/api/credits/free-tier-config");
      if (data.success && data.config) {
        setFreeTierConfig({
          default_credits: data.config.default_credits ?? 100,
          trial_days: data.config.trial_days ?? 15,
          restrict_after_trial: data.config.restrict_after_trial ?? true,
        });
      }
    } catch (error: any) {
      console.error("Erro ao carregar configurações do Free Tier", error);
    }
  };

  const handleSaveFreeTierConfig = async () => {
    setSavingConfig(true);
    try {
      const data = await apiService.post(
        "/api/credits/free-tier-config",
        freeTierConfig,
      );
      if (data.success) {
        showToastSuccess(t("Configurações do Free Tier salvas com sucesso!"));
      } else {
        throw new Error(data.error || t("Falha ao salvar"));
      }
    } catch (error: any) {
      showToastError(
        error.message || t("Erro ao salvar configurações do Free Tier"),
      );
    } finally {
      setSavingConfig(false);
    }
  };

  const loadWhatsappCrmPlanConfig = async () => {
    try {
      const data = await apiService.get("/api/admin/whatsapp-crm-plan-config");
      if (data.success && data.config) {
        setWhatsappCrmPlanConfig({
          base_price: (data.config.base_price_cents / 100).toFixed(2),
          included_seats: data.config.included_seats ?? 3,
          extra_seat_price: (data.config.extra_seat_price_cents / 100).toFixed(
            2,
          ),
          included_whatsapp_sends: data.config.included_whatsapp_sends ?? 0,
          included_email_sends: data.config.included_email_sends ?? 0,
        });
      }
    } catch (error: any) {
      console.error(
        "Erro ao carregar configuração do plano Virtus Chat",
        error,
      );
    }
  };

  const handleSaveWhatsappCrmPlanConfig = async () => {
    const basePriceCents = Math.round(
      parseFloat(whatsappCrmPlanConfig.base_price.replace(",", ".")) * 100,
    );
    const extraSeatPriceCents = Math.round(
      parseFloat(whatsappCrmPlanConfig.extra_seat_price.replace(",", ".")) *
        100,
    );
    if (
      Number.isNaN(basePriceCents) ||
      basePriceCents < 0 ||
      Number.isNaN(extraSeatPriceCents) ||
      extraSeatPriceCents < 0
    ) {
      showToastError(t("Informe valores válidos para os preços"));
      return;
    }

    setSavingWhatsappCrmPlanConfig(true);
    try {
      const data = await apiService.put("/api/admin/whatsapp-crm-plan-config", {
        base_price_cents: basePriceCents,
        included_seats: whatsappCrmPlanConfig.included_seats,
        extra_seat_price_cents: extraSeatPriceCents,
        included_whatsapp_sends: whatsappCrmPlanConfig.included_whatsapp_sends,
        included_email_sends: whatsappCrmPlanConfig.included_email_sends,
      });
      if (data.success) {
        showToastSuccess(t("Preço do plano Virtus Chat salvo com sucesso!"));
      } else {
        throw new Error(data.error || t("Falha ao salvar"));
      }
    } catch (error: any) {
      showToastError(
        error.message || t("Erro ao salvar preço do plano Virtus Chat"),
      );
    } finally {
      setSavingWhatsappCrmPlanConfig(false);
    }
  };

  const loadCreditCosts = async () => {
    setLoadingCreditCosts(true);
    try {
      const data = await apiService.get("/api/admin/credit-costs");
      if (data.success) {
        const costs = data.costs || [];
        setCreditCosts(costs);
        setEditingCreditCosts(
          Object.fromEntries(
            costs.map((c: { usage_type: string; cost_credits: number }) => [
              c.usage_type,
              String(c.cost_credits),
            ]),
          ),
        );
      }
    } catch (error: any) {
      showToastError(error.message || t("Erro ao carregar tabela de créditos"));
    } finally {
      setLoadingCreditCosts(false);
    }
  };

  const handleSaveCreditCost = async (usageType: string) => {
    const cost = parseInt(editingCreditCosts[usageType], 10);
    if (Number.isNaN(cost) || cost < 0) {
      showToastError(t("Informe um número inteiro válido"));
      return;
    }
    setSavingCreditCostUsageType(usageType);
    try {
      const data = await apiService.put("/api/admin/credit-costs", {
        usage_type: usageType,
        cost_credits: cost,
      });
      if (!data.success) throw new Error(data.error || t("Falha ao salvar"));
      setCreditCosts((prev) =>
        prev.map((c) => (c.usage_type === usageType ? data.cost : c)),
      );
      showToastSuccess(t("Preço atualizado!"));
    } catch (error: any) {
      showToastError(error.message || t("Erro ao salvar preço"));
    } finally {
      setSavingCreditCostUsageType(null);
    }
  };

  const loadCreditOverrides = async (
    scope: "organization" | "client",
    id: string,
  ) => {
    if (!id) {
      setCreditOverrides([]);
      return;
    }
    setLoadingCreditOverrides(true);
    try {
      const url =
        scope === "organization"
          ? `/api/admin/organizations/${id}/credit-costs`
          : `/api/admin/client-companies/${id}/credit-costs`;
      const data = await apiService.get(url);
      setCreditOverrides(data.success ? data.overrides || [] : []);
    } catch (error: any) {
      setCreditOverrides([]);
      showToastError(error.message || t("Erro ao carregar overrides"));
    } finally {
      setLoadingCreditOverrides(false);
    }
  };

  const handleAddOverride = async () => {
    const targetId =
      creditCostScope === "organization"
        ? creditOverrideOrgId
        : creditOverrideClientId;
    if (!targetId) {
      showToastError(
        creditCostScope === "organization"
          ? t("Selecione uma organização")
          : t("Selecione uma empresa cliente"),
      );
      return;
    }
    const cost = parseInt(newOverrideForm.cost_credits, 10);
    if (!newOverrideForm.usage_type || Number.isNaN(cost) || cost < 0) {
      showToastError(t("Informe o tipo de uso e um valor válido"));
      return;
    }
    setSavingOverride(true);
    try {
      const url =
        creditCostScope === "organization"
          ? `/api/admin/organizations/${targetId}/credit-costs`
          : `/api/admin/client-companies/${targetId}/credit-costs`;
      const data = await apiService.put(url, {
        usage_type: newOverrideForm.usage_type,
        cost_credits: cost,
      });
      if (!data.success) throw new Error(data.error || t("Falha ao salvar"));
      showToastSuccess(t("Override salvo!"));
      setNewOverrideForm({ usage_type: "", cost_credits: "" });
      loadCreditOverrides(
        creditCostScope as "organization" | "client",
        targetId,
      );
    } catch (error: any) {
      showToastError(error.message || t("Erro ao salvar override"));
    } finally {
      setSavingOverride(false);
    }
  };

  const handleDeleteOverride = async (usageType: string) => {
    const targetId =
      creditCostScope === "organization"
        ? creditOverrideOrgId
        : creditOverrideClientId;
    if (!targetId) return;
    setDeletingOverrideUsageType(usageType);
    try {
      const url =
        creditCostScope === "organization"
          ? `/api/admin/organizations/${targetId}/credit-costs/${usageType}`
          : `/api/admin/client-companies/${targetId}/credit-costs/${usageType}`;
      const data = await apiService.delete(url);
      if (!data.success) throw new Error(data.error || t("Falha ao remover"));
      showToastSuccess(t("Override removido — voltou a valer o preço padrão."));
      setCreditOverrides((prev) =>
        prev.filter((o) => o.usage_type !== usageType),
      );
    } catch (error: any) {
      showToastError(error.message || t("Erro ao remover override"));
    } finally {
      setDeletingOverrideUsageType(null);
    }
  };

  useEffect(() => {
    loadCompanies();
    loadFreeTierConfig();
    loadWhatsappCrmPlanConfig();
  }, []);

  useEffect(() => {
    if (showCreditCostsModal && creditCosts.length === 0) {
      loadCreditCosts();
    }
  }, [showCreditCostsModal]);

  useEffect(() => {
    if (creditCostScope === "organization") {
      loadCreditOverrides("organization", creditOverrideOrgId);
    } else if (creditCostScope === "client") {
      loadCreditOverrides("client", creditOverrideClientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditCostScope, creditOverrideOrgId, creditOverrideClientId]);

  const handleCreateCompany = async () => {
    if (!formData.name.trim()) {
      showToastError(t("O nome da organização é obrigatório"));
      return;
    }

    setSubmitting(true);
    try {
      const data = await apiService.post("/api/admin/companies", {
        name: formData.name,
      });

      if (!data.success) {
        throw new Error(data.error || t("Erro ao criar organização"));
      }

      showToastSuccess(t("Organização criada com sucesso!"));
      setShowCreateModal(false);
      setFormData({ name: "", organization_id: "", is_enterprise: false });
      loadCompanies();
    } catch (error: any) {
      showToastError(error.message || t("Erro ao criar organização"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCompany = async () => {
    if (!selectedCompany || !formData.name.trim()) {
      showToastError(t("O nome da organização é obrigatório"));
      return;
    }

    setSubmitting(true);
    try {
      const data = await apiService.put(
        `/api/admin/companies/${selectedCompany.id}`,
        { name: formData.name, is_enterprise: formData.is_enterprise },
      );

      if (!data.success) {
        throw new Error(data.error || t("Erro ao atualizar organização"));
      }

      showToastSuccess(t("Organização atualizada com sucesso!"));
      setShowEditModal(false);
      setSelectedCompany(null);
      setFormData({ name: "", organization_id: "", is_enterprise: false });
      loadCompanies();
    } catch (error: any) {
      showToastError(error.message || t("Erro ao atualizar organização"));
    } finally {
      setSubmitting(false);
    }
  };

  const deferredSearchQuery = useDeferredValue(searchQuery);

  const filteredCompanies = useMemo(() => {
    const q = deferredSearchQuery.trim().toLowerCase();
    return companies.filter((c) => {
      const matchesSearch =
        !q ||
        c.name.toLowerCase().includes(q) ||
        (c.cnpj && c.cnpj.toLowerCase().includes(q));
      const matchesPlan =
        !planFilter ||
        (planFilter === "enterprise" ? c.is_enterprise : !c.is_enterprise);
      return matchesSearch && matchesPlan;
    });
  }, [companies, deferredSearchQuery, planFilter]);

  const enterpriseCount = useMemo(() => {
    return companies.filter((c) => c.is_enterprise).length;
  }, [companies]);

  const paginatedCompanies = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredCompanies.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredCompanies, currentPage, itemsPerPage]);

  const openEditModal = (company: GlobalCompany) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      organization_id: "",
      is_enterprise: company.is_enterprise || false,
    });
    setShowEditModal(true);
  };

  const openCreditsModal = (company: GlobalCompany) => {
    setSelectedCompany(company);
    setCreditsFormData({
      amount: "",
      client_company_id: "",
      description: "",
      product: "",
    });
    setShowCreditsModal(true);
  };

  const handleGrantCredits = async () => {
    if (!selectedCompany) return;

    const amount = parseInt(creditsFormData.amount, 10);
    if (!amount || amount <= 0) {
      showToastError(t("Informe uma quantidade de créditos válida"));
      return;
    }

    setGrantingCredits(true);
    try {
      const data = await apiService.post(
        `/api/admin/companies/${selectedCompany.id}/grant-credits`,
        {
          amount,
          client_company_id: creditsFormData.client_company_id || undefined,
          description: creditsFormData.description || undefined,
          product: creditsFormData.product || undefined,
        },
      );

      if (!data.success) {
        throw new Error(data.error || t("Erro ao conceder créditos"));
      }

      showToastSuccess(
        creditsFormData.product
          ? t("{amount} {unit} concedidos! Novo saldo de pacotes: {balance}", {
              amount,
              unit:
                creditsFormData.product === "whatsapp"
                  ? t("envios de WhatsApp")
                  : t("envios de e-mail"),
              balance: data.new_balance,
            })
          : t("{amount} créditos concedidos! Novo saldo: {balance}", {
              amount,
              balance: data.new_balance,
            }),
      );
      setShowCreditsModal(false);
      setSelectedCompany(null);
      setCreditsFormData({
        amount: "",
        client_company_id: "",
        description: "",
        product: "",
      });
      loadCompanies();
    } catch (error: any) {
      showToastError(error.message || t("Erro ao conceder créditos"));
    } finally {
      setGrantingCredits(false);
    }
  };

  return (
    <PageLayout
      title={t("Super Admin: Organizações Globais")}
      subtitle={t(
        "Visão mestre de todas as organizações (contas principais) cadastradas no sistema.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-4 py-4"
      loading={loading}
      skeleton={<AdminCompaniesSkeleton />}
      actions={
        <div className="flex items-center gap-2">
          <span className="hidden md:flex items-center gap-2 h-8 px-3 rounded-full text-[10px] font-black uppercase tracking-widest bg-purple-50 text-purple-700 border border-purple-100">
            {t("Total de Organizações")}
            <span className="text-[10px] py-0.5 px-2 rounded-full font-bold bg-purple-200 text-purple-800">
              {companies.length}
            </span>
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setCreditCostScope("global");
              setShowCreditCostsModal(true);
            }}
            icon={<Coins className={HEADER_SECONDARY_ICON} />}
            className={HEADER_SECONDARY_BUTTON}
          >
            <span className="hidden sm:inline text-xs">
              {t("Tabela de Créditos")}
            </span>
            <span className="sm:hidden text-xs">{t("Créditos")}</span>
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => {
              setFormData({
                name: "",
                organization_id: "",
                is_enterprise: false,
              });
              setShowCreateModal(true);
            }}
            icon={<Plus className={HEADER_ICON} />}
            className={HEADER_PRIMARY_BUTTON}
          >
            <span className="hidden sm:inline text-xs">
              {t("Nova Organização")}
            </span>
            <span className="sm:hidden text-xs">{t("Nova")}</span>
          </Button>
        </div>
      }
    >
      <div className="relative z-10 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700">
        {/* Filter bar — busca + plano, grudada na description bar, mesmo
            padrão sticky das demais páginas (Posts/AdsList/CampaignLeads/
            OrganizationUsers). */}
        <div className="sticky top-0 z-30 -mt-4 -mx-4 px-4 py-2 mb-4 bg-white/95 backdrop-blur-md border-b border-purple-200 flex flex-col gap-2">
          <div className="flex flex-row flex-wrap gap-2 items-center w-full">
            <div className="flex-1 min-w-[200px]">
              <Input
                type="text"
                placeholder={t("Buscar por organização ou CNPJ...")}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                inputSize="sm"
                icon={<Search className="h-4 w-4 text-gray-400" />}
                className="w-full h-8! sm:h-9!"
              />
            </div>
            <div className="w-[150px] sm:w-[170px] shrink-0">
              <Select
                value={planFilter}
                onChange={(v) => {
                  setPlanFilter(v as "" | "enterprise" | "standard");
                  setCurrentPage(1);
                }}
                showSelectedCount
                size="sm"
                className="h-8! sm:h-9!"
                options={[
                  {
                    value: "",
                    label: t("Planos"),
                    icon: <Building2 />,
                    count: companies.length,
                  },
                  {
                    value: "enterprise",
                    label: t("Enterprise"),
                    icon: <Award />,
                    count: enterpriseCount,
                  },
                  {
                    value: "standard",
                    label: t("Standard"),
                    icon: <Zap />,
                    count: companies.length - enterpriseCount,
                  },
                ]}
              />
            </div>
          </div>

          {(searchQuery || planFilter) && (
            <div className="pt-2 border-t border-purple-100 flex gap-2 items-center justify-between text-xs text-gray-600 flex-wrap">
              <div className="flex flex-wrap items-center gap-2">
                <span>
                  {t("Exibindo")}{" "}
                  <strong className="text-gray-900">
                    {filteredCompanies.length}
                  </strong>{" "}
                  {t("de")} {companies.length} {t("organizações")}
                </span>
                {searchQuery && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
                    {t("Busca")}: {searchQuery}
                    <button
                      onClick={() => setSearchQuery("")}
                      className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {planFilter && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
                    {t("Plano")}:{" "}
                    {planFilter === "enterprise"
                      ? t("Enterprise")
                      : t("Standard")}
                    <button
                      onClick={() => setPlanFilter("")}
                      className="hover:bg-purple-200 rounded-full p-0.5 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setPlanFilter("");
                }}
                className="text-purple-600 hover:text-purple-700 font-black uppercase tracking-tighter shrink-0"
              >
                {t("Limpar filtros")}
              </button>
            </div>
          )}
        </div>

        {/* Card de Configuração do Free Tier — migrado do tema escuro para o
            padrão claro/purple do resto do app (era o único bloco em tema
            escuro nesta página; não havia justificativa funcional para a
            distinção visual). */}
        {/* Compactado: título+descrição viraram uma linha só (era 2, com
            parágrafo próprio); layout vira horizontal a partir de md (era
            só a partir de lg — em qualquer largura entre 768-1024px ficava
            tudo empilhado à toa); campos e botão reduzidos de h-10 pra h-9,
            gaps apertados. */}
        <div className="bg-white rounded-2xl p-3.5 sm:p-4 shadow-xs border border-purple-200 relative overflow-hidden group mb-4">
          <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 w-64 h-64 bg-indigo-100/60 rounded-full blur-3xl pointer-events-none group-hover:scale-110 transition-transform duration-700" />
          <div className="absolute bottom-0 left-0 translate-y-12 -translate-x-12 w-64 h-64 bg-purple-100/60 rounded-full blur-3xl pointer-events-none group-hover:scale-110 transition-transform duration-700" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
            <div className="flex items-center gap-2.5 shrink-0">
              <span className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg border border-indigo-100 shrink-0">
                <Zap className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <h2 className="text-xs font-black uppercase tracking-wider text-gray-900 leading-tight">
                  {t("Parâmetros de Teste (Free Trial)")}
                </h2>
                <p className="text-[10px] text-gray-400 font-medium truncate">
                  {t(
                    "Limitações e validade padrão do plano de experimentação para novas organizações.",
                  )}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end grow max-w-2xl">
              <div>
                <label className="block text-[9px] font-black uppercase tracking-wider text-gray-500 mb-1">
                  {t("Créditos Iniciais")}
                </label>
                <input
                  type="number"
                  value={freeTierConfig.default_credits}
                  onChange={(e) =>
                    setFreeTierConfig({
                      ...freeTierConfig,
                      default_credits: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full h-9 px-3 rounded-lg bg-gray-50/50 border border-purple-100 focus:border-indigo-400 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 text-gray-900 text-sm font-bold transition-colors"
                />
              </div>

              <div>
                <label className="block text-[9px] font-black uppercase tracking-wider text-gray-500 mb-1">
                  {t("Dias de Teste Grátis")}
                </label>
                <input
                  type="number"
                  value={freeTierConfig.trial_days}
                  onChange={(e) =>
                    setFreeTierConfig({
                      ...freeTierConfig,
                      trial_days: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full h-9 px-3 rounded-lg bg-gray-50/50 border border-purple-100 focus:border-indigo-400 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 text-gray-900 text-sm font-bold transition-colors"
                />
              </div>

              <div className="flex items-center h-9">
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={freeTierConfig.restrict_after_trial}
                    onChange={(e) =>
                      setFreeTierConfig({
                        ...freeTierConfig,
                        restrict_after_trial: e.target.checked,
                      })
                    }
                    className="w-4 h-4 rounded-sm border-gray-300 bg-white text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-[9px] font-black uppercase tracking-wider text-gray-600">
                    {t("Bloquear após prazo")}
                  </span>
                </label>
              </div>
            </div>

            <div className="shrink-0">
              <Button
                type="button"
                onClick={handleSaveFreeTierConfig}
                isLoading={savingConfig}
                className="w-full md:w-auto h-9 px-5 font-black text-xs uppercase tracking-widest bg-linear-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg shadow-lg shadow-indigo-500/20 transform hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                {t("Salvar Regras")}
              </Button>
            </div>
          </div>
        </div>

        {/* Preço do plano recorrente do Virtus Chat — mesmo padrão visual do card de Free
            Tier acima. Editar aqui NÃO altera assinaturas já ativas (preço fica congelado por
            cliente no momento da ativação); só vale pra novas ativações a partir de agora. */}
        <div className="bg-white rounded-2xl p-3.5 sm:p-4 shadow-xs border border-purple-200 relative overflow-hidden group mb-4">
          <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 w-64 h-64 bg-emerald-100/60 rounded-full blur-3xl pointer-events-none group-hover:scale-110 transition-transform duration-700" />
          <div className="absolute bottom-0 left-0 translate-y-12 -translate-x-12 w-64 h-64 bg-teal-100/60 rounded-full blur-3xl pointer-events-none group-hover:scale-110 transition-transform duration-700" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
            <div className="flex items-center gap-2.5 shrink-0">
              <span className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg border border-emerald-100 shrink-0">
                <MessageCircle className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <h2 className="text-xs font-black uppercase tracking-wider text-gray-900 leading-tight">
                  {t("Plano Virtus Chat (Assinatura Recorrente)")}
                </h2>
                <p className="text-[10px] text-gray-400 font-medium truncate">
                  {t(
                    "Preço base mensal, assentos inclusos e valor do assento extra — não afeta assinaturas já ativas.",
                  )}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3 items-end grow max-w-4xl">
              <div>
                <label className="block text-[9px] font-black uppercase tracking-wider text-gray-500 mb-1">
                  {t("Preço Base (R$/mês)")}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={whatsappCrmPlanConfig.base_price}
                  onChange={(e) =>
                    setWhatsappCrmPlanConfig({
                      ...whatsappCrmPlanConfig,
                      base_price: e.target.value,
                    })
                  }
                  className="w-full h-9 px-3 rounded-lg bg-gray-50/50 border border-purple-100 focus:border-emerald-400 focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 text-gray-900 text-sm font-bold transition-colors"
                />
              </div>

              <div>
                <label className="block text-[9px] font-black uppercase tracking-wider text-gray-500 mb-1">
                  {t("Assentos Inclusos")}
                </label>
                <input
                  type="number"
                  min={0}
                  value={whatsappCrmPlanConfig.included_seats}
                  onChange={(e) =>
                    setWhatsappCrmPlanConfig({
                      ...whatsappCrmPlanConfig,
                      included_seats: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full h-9 px-3 rounded-lg bg-gray-50/50 border border-purple-100 focus:border-emerald-400 focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 text-gray-900 text-sm font-bold transition-colors"
                />
              </div>

              <div>
                <label className="block text-[9px] font-black uppercase tracking-wider text-gray-500 mb-1">
                  {t("Assento Extra (R$/mês)")}
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={whatsappCrmPlanConfig.extra_seat_price}
                  onChange={(e) =>
                    setWhatsappCrmPlanConfig({
                      ...whatsappCrmPlanConfig,
                      extra_seat_price: e.target.value,
                    })
                  }
                  className="w-full h-9 px-3 rounded-lg bg-gray-50/50 border border-purple-100 focus:border-emerald-400 focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 text-gray-900 text-sm font-bold transition-colors"
                />
              </div>
              <div>
                <label
                  className="block text-[9px] font-black uppercase tracking-wider text-gray-500 mb-1"
                  title={t(
                    "Franquia mensal da assinatura. Redefinida a cada pagamento confirmado; não acumula e não depende dos assentos.",
                  )}
                >
                  {t("Franquia WhatsApp (envios/mês)")}
                </label>
                <input
                  type="number"
                  min={0}
                  value={whatsappCrmPlanConfig.included_whatsapp_sends}
                  onChange={(e) =>
                    setWhatsappCrmPlanConfig({
                      ...whatsappCrmPlanConfig,
                      included_whatsapp_sends: Math.max(
                        0,
                        parseInt(e.target.value) || 0,
                      ),
                    })
                  }
                  className="w-full h-9 px-3 rounded-lg bg-gray-50/50 border border-purple-100 focus:border-emerald-400 focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 text-gray-900 text-sm font-bold transition-colors"
                />
              </div>
              <div>
                <label
                  className="block text-[9px] font-black uppercase tracking-wider text-gray-500 mb-1"
                  title={t(
                    "Franquia mensal da assinatura. Redefinida a cada pagamento confirmado; não acumula e não depende dos assentos.",
                  )}
                >
                  {t("Franquia E-mail (envios/mês)")}
                </label>
                <input
                  type="number"
                  min={0}
                  value={whatsappCrmPlanConfig.included_email_sends}
                  onChange={(e) =>
                    setWhatsappCrmPlanConfig({
                      ...whatsappCrmPlanConfig,
                      included_email_sends: Math.max(
                        0,
                        parseInt(e.target.value) || 0,
                      ),
                    })
                  }
                  className="w-full h-9 px-3 rounded-lg bg-gray-50/50 border border-purple-100 focus:border-emerald-400 focus:outline-hidden focus:ring-2 focus:ring-emerald-500/20 text-gray-900 text-sm font-bold transition-colors"
                />
              </div>
            </div>

            <div className="shrink-0">
              <Button
                type="button"
                onClick={handleSaveWhatsappCrmPlanConfig}
                isLoading={savingWhatsappCrmPlanConfig}
                className="w-full md:w-auto h-9 px-5 font-black text-xs uppercase tracking-widest bg-linear-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-lg shadow-lg shadow-emerald-500/20 transform hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                {t("Salvar Preços")}
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {paginatedCompanies.map((company) => (
            <Card
              key={company.id}
              className="p-0 bg-white/80 backdrop-blur-xl border-purple-100 shadow-xl shadow-purple-500/5 rounded-3xl overflow-hidden group transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/10 hover:scale-[1.02]"
            >
              <div className="relative p-4 sm:p-5">
                <div className="absolute top-4 right-4">
                  <div className="flex items-center gap-1.5 bg-green-50 px-2.5 py-1 rounded-full border border-green-100 shadow-xs shadow-green-200/50">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-[9px] font-black text-green-700 uppercase tracking-widest">
                      {t("Ativo")}
                    </span>
                  </div>
                </div>

                {/* Header horizontal (era empilhado/centralizado com
                      mb-8 — mesmo tratamento aplicado em UserProfile.tsx;
                      aqui o ganho se multiplica por card no grid). */}
                <div className="flex items-center gap-3 mb-2 pr-20">
                  <div
                    className={`w-12 h-12 rounded-xl bg-linear-to-br ${getCompanyColor(company.name)} p-0.5 shadow-md shrink-0`}
                  >
                    <div className="w-full h-full rounded-lg bg-white/10 backdrop-blur-md flex items-center justify-center text-white text-base font-black">
                      {getInitials(company.name)}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-black text-gray-900 tracking-tight truncate">
                      {company.name}
                    </h3>
                    {company.cnpj && (
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest truncate">
                        {company.cnpj}
                      </p>
                    )}
                  </div>
                </div>

                {/* Badges de plano/créditos + "Unidades" — eram 2 linhas
                      empilhadas, agora dividem a mesma linha
                      (justify-between), removendo uma linha inteira. */}
                <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                  <div className="flex gap-1.5 flex-wrap">
                    {company.is_enterprise ? (
                      <span className="px-2.5 py-1 bg-linear-to-r from-amber-200 to-yellow-400 text-yellow-900 text-[9px] font-black uppercase tracking-widest rounded-full shadow-xs">
                        {t("Plano Enterprise")}
                      </span>
                    ) : (
                      company.credits_balance !== undefined && (
                        <>
                          <span
                            className="px-2.5 py-1 bg-emerald-50 text-emerald-600 border border-emerald-100 text-[10px] font-black tracking-wider rounded-full shadow-xs"
                            title={t("Restantes")}
                          >
                            {t("{count} Créditos", {
                              count: company.credits_balance,
                            })}
                          </span>
                          <span
                            className="px-2.5 py-1 bg-rose-50 text-rose-600 border border-rose-100 text-[10px] font-black tracking-wider rounded-full shadow-xs"
                            title={t("Consumidos")}
                          >
                            {t("{count} Consumidos", {
                              count: company.credits_consumed || 0,
                            })}
                          </span>
                          {company.faturamento !== undefined && (
                            <span
                              className="px-2.5 py-1 bg-blue-50 text-blue-600 border border-blue-100 text-[10px] font-black tracking-wider rounded-full shadow-xs"
                              title={t("Faturamento Total")}
                            >
                              R${" "}
                              {company.faturamento.toFixed(2).replace(".", ",")}
                            </span>
                          )}
                        </>
                      )
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setSelectedCompany(company);
                      setShowUnitsModal(true);
                    }}
                    title={t("Unidades Cadastradas")}
                    className="group/units flex items-center gap-2 text-[11px] font-black px-3 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100 uppercase tracking-widest hover:bg-purple-600 hover:text-white hover:border-purple-600 transition-all shadow-xs shrink-0"
                  >
                    {company.clients_count} {t("Unidades")}
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400 group-hover/units:bg-white animate-pulse" />
                  </button>
                </div>

                <div className="space-y-3">
                  {/* Métricas unificadas — eram 2 grids separados (um solto
                        + um dentro de um card branco com borda/header
                        próprios); os ícones já distinguem cada categoria,
                        então o rótulo de seção extra e o container aninhado
                        eram redundantes. */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-gray-50/50 rounded-xl p-2.5 flex flex-col items-center justify-center gap-1 border border-transparent group-hover:border-purple-100 group-hover:bg-white transition-all">
                      <div className="p-1.5 bg-blue-100 rounded-lg text-blue-600">
                        <Users className="h-3.5 w-3.5" />
                      </div>
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter leading-none">
                        {t("Membros")}
                      </p>
                      <p className="text-sm font-black text-gray-900">
                        {company.users_count}
                      </p>
                    </div>
                    <div className="bg-gray-50/50 rounded-xl p-2.5 flex flex-col items-center justify-center gap-1 border border-transparent group-hover:border-purple-100 group-hover:bg-white transition-all">
                      <div className="p-1.5 bg-emerald-100 rounded-lg text-emerald-600">
                        <UserCheck className="h-3.5 w-3.5" />
                      </div>
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter leading-none">
                        {t("Leads")}
                      </p>
                      <p className="text-sm font-black text-gray-900">
                        {company.campaign_leads}
                      </p>
                    </div>
                    <div className="bg-gray-50/50 rounded-xl p-2.5 flex flex-col items-center justify-center gap-1 border border-transparent group-hover:border-purple-100 group-hover:bg-white transition-all">
                      <div className="p-1.5 bg-purple-100 rounded-lg text-purple-600">
                        <Library className="h-3.5 w-3.5" />
                      </div>
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter leading-none">
                        {t("Biblioteca")}
                      </p>
                      <p className="text-sm font-black text-gray-900">
                        {company.content_library_count}
                      </p>
                    </div>
                    <div className="bg-gray-50/50 rounded-xl p-2.5 flex flex-col items-center justify-center gap-1 border border-transparent group-hover:border-purple-100 group-hover:bg-white transition-all">
                      <div className="p-1.5 bg-amber-100 rounded-lg text-amber-600">
                        <FileText className="h-3.5 w-3.5" />
                      </div>
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter leading-none">
                        {t("Posts")}
                      </p>
                      <p className="text-sm font-black text-gray-900">
                        {company.posts_published}
                        <span className="text-[10px] text-gray-400 font-bold">
                          /{company.posts_created}
                        </span>
                      </p>
                    </div>
                    <div className="bg-gray-50/50 rounded-xl p-2.5 flex flex-col items-center justify-center gap-1 border border-transparent group-hover:border-purple-100 group-hover:bg-white transition-all">
                      <div className="p-1.5 bg-pink-100 rounded-lg text-pink-600">
                        <Image className="h-3.5 w-3.5" />
                      </div>
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter leading-none">
                        {t("Imagens")}
                      </p>
                      <p className="text-sm font-black text-gray-900">
                        {company.images_created}
                      </p>
                    </div>
                    <div className="bg-gray-50/50 rounded-xl p-2.5 flex flex-col items-center justify-center gap-1 border border-transparent group-hover:border-purple-100 group-hover:bg-white transition-all">
                      <div className="p-1.5 bg-indigo-100 rounded-lg text-indigo-600">
                        <Video className="h-3.5 w-3.5" />
                      </div>
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter leading-none">
                        {t("Vídeos")}
                      </p>
                      <p className="text-sm font-black text-gray-900">
                        {company.videos_created}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-purple-50">
                    <div>
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                        {t("Desde")}
                      </p>
                      <p className="text-[10px] font-bold text-gray-600">
                        {new Date(company.created_at).toLocaleDateString(
                          "pt-BR",
                        )}{" "}
                        <span className="text-gray-400 font-medium normal-case">
                          (
                          {t("{count} dias", {
                            count: Math.max(
                              0,
                              Math.floor(
                                (new Date().getTime() -
                                  new Date(company.created_at).getTime()) /
                                  (1000 * 60 * 60 * 24),
                              ),
                            ),
                          })}
                          )
                        </span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openCreditsModal(company)}
                        title={t("Adicionar Créditos")}
                        className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all transform hover:rotate-6 active:scale-95 shadow-xs border border-emerald-100"
                      >
                        <Coins className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(company)}
                        title={t("Editar Organização")}
                        className="p-2.5 rounded-xl bg-purple-50 text-purple-600 hover:bg-purple-600 hover:text-white transition-all transform hover:rotate-6 active:scale-95 shadow-xs border border-purple-100"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {paginatedCompanies.length > 0 && (
          <div className="mt-8 flex justify-center w-full">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.max(
                1,
                Math.ceil(filteredCompanies.length / itemsPerPage),
              )}
              totalItems={filteredCompanies.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(limit) => {
                setItemsPerPage(limit);
                setCurrentPage(1);
              }}
              itemsPerPageOptions={[12, 24, 48, 96]}
            />
          </div>
        )}

        {filteredCompanies.length === 0 && !loading && (
          <EmptyState
            icon={<Search className="w-16 h-16 text-gray-200" />}
            title={t("Nenhum resultado")}
            description={t(
              "Nenhuma organização encontrada para esses filtros.",
            )}
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  setSearchQuery("");
                  setPlanFilter("");
                }}
                className="rounded-xl h-10 px-6 text-xs font-black uppercase tracking-widest"
              >
                {t("Limpar filtros")}
              </Button>
            }
          />
        )}

        {companies.length === 0 && (
          <EmptyState
            icon={<Building2 className="w-16 h-16 text-gray-200" />}
            title={t("Nenhuma organização cadastrada")}
            description={t(
              "Nenhuma organização global foi cadastrada no sistema ainda.",
            )}
          />
        )}

        <Modal
          open={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setFormData({
              name: "",
              organization_id: "",
              is_enterprise: false,
            });
          }}
          title={t("Nova Organização")}
          subtitle={t(
            "Cadastre uma nova organização (conta mestra) no sistema.",
          )}
          maxWidth="md"
          footer={
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({
                    name: "",
                    organization_id: "",
                    is_enterprise: false,
                  });
                }}
              >
                {t("Cancelar")}
              </Button>
              <Button
                type="button"
                onClick={handleCreateCompany}
                disabled={!formData.name.trim() || submitting}
                isLoading={submitting}
              >
                {t("Criar Organização")}
              </Button>
            </div>
          }
        >
          <div className="space-y-6">
            <Input
              label={t("Nome da Organização")}
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder={t("Ex: Grupo Virtus")}
              className="rounded-2xl"
            />
            <p className="text-xs text-gray-500 italic">
              {t(
                "* A criação de organizações requer um usuário proprietário. Use o gerenciador de usuários para vincular proprietários a novas organizações.",
              )}
            </p>
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
              <input
                type="checkbox"
                id="is_enterprise"
                checked={formData.is_enterprise}
                onChange={(e) =>
                  setFormData({ ...formData, is_enterprise: e.target.checked })
                }
                className="w-5 h-5 rounded-sm border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <label
                htmlFor="is_enterprise"
                className="flex flex-col cursor-pointer"
              >
                <span className="text-sm font-black text-gray-900">
                  {t("Plano Enterprise")}
                </span>
                <span className="text-xs text-gray-500">
                  {t(
                    "Isenta a organização de cobranças e bloqueios de créditos.",
                  )}
                </span>
              </label>
            </div>
          </div>
        </Modal>

        <Modal
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCompany(null);
            setFormData({
              name: "",
              organization_id: "",
              is_enterprise: false,
            });
          }}
          title={t("Editar Organização")}
          subtitle={
            selectedCompany
              ? t("Editando: {name}", { name: selectedCompany.name })
              : ""
          }
          maxWidth="md"
          footer={
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCompany(null);
                  setFormData({
                    name: "",
                    organization_id: "",
                    is_enterprise: false,
                  });
                }}
              >
                {t("Cancelar")}
              </Button>
              <Button
                type="button"
                onClick={handleUpdateCompany}
                disabled={!formData.name.trim() || submitting}
                isLoading={submitting}
              >
                {t("Salvar Alterações")}
              </Button>
            </div>
          }
        >
          <div className="space-y-6">
            <Input
              label={t("Nome da Organização")}
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder={t("Ex: Grupo Virtus")}
              className="rounded-2xl"
            />
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100">
              <input
                type="checkbox"
                id="edit_is_enterprise"
                checked={formData.is_enterprise}
                onChange={(e) =>
                  setFormData({ ...formData, is_enterprise: e.target.checked })
                }
                className="w-5 h-5 rounded-sm border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <label
                htmlFor="edit_is_enterprise"
                className="flex flex-col cursor-pointer"
              >
                <span className="text-sm font-black text-gray-900">
                  {t("Plano Enterprise")}
                </span>
                <span className="text-xs text-gray-500">
                  {t(
                    "Isenta a organização de cobranças e bloqueios de créditos.",
                  )}
                </span>
              </label>
            </div>
          </div>
        </Modal>

        <Modal
          open={showCreditsModal}
          onClose={() => {
            setShowCreditsModal(false);
            setSelectedCompany(null);
            setCreditsFormData({
              amount: "",
              client_company_id: "",
              description: "",
              product: "",
            });
          }}
          title={t("Adicionar Créditos")}
          subtitle={
            selectedCompany
              ? t("Concedendo créditos para: {name}", {
                  name: selectedCompany.name,
                })
              : ""
          }
          maxWidth="md"
          footer={
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreditsModal(false);
                  setSelectedCompany(null);
                  setCreditsFormData({
                    amount: "",
                    client_company_id: "",
                    description: "",
                    product: "",
                  });
                }}
              >
                {t("Cancelar")}
              </Button>
              <Button
                type="button"
                onClick={handleGrantCredits}
                disabled={!creditsFormData.amount || grantingCredits}
                isLoading={grantingCredits}
              >
                {creditsFormData.product
                  ? t("Conceder Envios")
                  : t("Conceder Créditos")}
              </Button>
            </div>
          }
        >
          <div className="space-y-6">
            <Select
              label={t("O que conceder")}
              value={creditsFormData.product}
              onChange={(v) =>
                setCreditsFormData({
                  ...creditsFormData,
                  product: v as "" | "whatsapp" | "email",
                })
              }
              options={[
                { value: "", label: t("Créditos de IA") },
                {
                  value: "whatsapp",
                  label: t("Envios de WhatsApp (templates)"),
                },
                { value: "email", label: t("Envios de e-mail") },
              ]}
            />
            {creditsFormData.product && (
              <p className="-mt-4 text-xs text-gray-500 dark:text-gray-400">
                {t(
                  "Os envios entram como pacote: liberam na hora e não expiram. A franquia mensal continua sendo renovada a cada pagamento da assinatura.",
                )}
              </p>
            )}

            <Input
              label={
                creditsFormData.product
                  ? t("Quantidade de envios")
                  : t("Quantidade de Créditos")
              }
              type="number"
              min="1"
              value={creditsFormData.amount}
              onChange={(e) =>
                setCreditsFormData({
                  ...creditsFormData,
                  amount: e.target.value,
                })
              }
              placeholder={t("Ex: 300")}
              className="rounded-2xl"
            />

            {selectedCompany && selectedCompany.client_companies.length > 0 && (
              <Select
                label={t("Destino")}
                value={creditsFormData.client_company_id}
                onChange={(v) =>
                  setCreditsFormData({
                    ...creditsFormData,
                    client_company_id: v as string,
                  })
                }
                options={[
                  { value: "", label: t("Organização (faturamento próprio)") },
                  ...selectedCompany.client_companies.map((unit) => ({
                    value: String(unit.id),
                    label: unit.name,
                  })),
                ]}
              />
            )}

            <Input
              label={t("Observação (opcional)")}
              value={creditsFormData.description}
              onChange={(e) =>
                setCreditsFormData({
                  ...creditsFormData,
                  description: e.target.value,
                })
              }
              placeholder={t("Ex: Cortesia para parceiro")}
              className="rounded-2xl"
            />
          </div>
        </Modal>

        <Modal
          open={showUnitsModal}
          onClose={() => {
            setShowUnitsModal(false);
            setSelectedCompany(null);
          }}
          title={t("Unidades: {name}", { name: selectedCompany?.name || "" })}
          subtitle={t(
            "Lista de todas as empresas cliente cadastradas sob esta organização.",
          )}
          maxWidth="md"
        >
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
            {selectedCompany?.client_companies &&
            selectedCompany.client_companies.length > 0 ? (
              <div className="grid gap-3">
                {selectedCompany.client_companies.map((unit) => (
                  <div
                    key={unit.id}
                    className="flex items-center justify-between p-4 rounded-2xl bg-gray-50 border border-purple-50 hover:border-purple-200 hover:bg-white transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center font-black group-hover:scale-110 transition-transform">
                        {unit.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-black text-gray-900 group-hover:text-purple-700 transition-colors uppercase tracking-tight">
                          {unit.name}
                        </p>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                          {t("ID Unidade: {id}", { id: unit.id })}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-1 justify-end items-center gap-6 ml-4">
                      <div className="flex gap-4">
                        <div className="flex flex-col">
                          <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">
                            {t("Faturamento")}
                          </span>
                          <span className="text-xs font-black px-2 py-0.5 rounded-sm bg-gray-100 text-gray-700">
                            {unit.billing_context === "CLIENT"
                              ? t("Independente")
                              : t("Organização")}
                          </span>
                        </div>
                        {unit.billing_context === "CLIENT" && (
                          <div className="flex flex-col gap-2">
                            <div className="flex flex-col">
                              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">
                                {t("Restantes")}
                              </span>
                              <span
                                className="text-xs font-black px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-600 border border-emerald-100"
                                title={t("Restantes")}
                              >
                                {unit.credits_balance || 0}
                              </span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">
                                {t("Consumidos")}
                              </span>
                              <span
                                className="text-xs font-black px-2 py-0.5 rounded-sm bg-rose-50 text-rose-600 border border-rose-100"
                                title={t("Consumidos")}
                              >
                                {unit.credits_consumed || 0}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="text-right flex flex-col items-end border-l border-gray-200 pl-6">
                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1">
                          {t("Data Cadastro")}
                        </p>
                        <div className="flex items-center gap-2 px-3 py-1 bg-white border border-purple-50 rounded-lg text-xs font-black text-purple-600 tracking-tight">
                          {new Date(unit.created_at).toLocaleDateString(
                            "pt-BR",
                          )}
                        </div>
                        <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mt-1">
                          {t("{count} dias de uso", {
                            count: Math.max(
                              0,
                              Math.floor(
                                (new Date().getTime() -
                                  new Date(unit.created_at).getTime()) /
                                  (1000 * 60 * 60 * 24),
                              ),
                            ),
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 bg-gray-50 rounded-4xl border-2 border-dashed border-purple-100">
                <div className="p-4 bg-purple-50 rounded-full mb-4">
                  <Users className="h-10 w-10 text-purple-300" />
                </div>
                <h4 className="text-lg font-black text-gray-900 uppercase tracking-tighter">
                  {t("Nenhuma Unidade")}
                </h4>
                <p className="text-gray-500 font-medium text-center px-6 text-sm">
                  {t(
                    "Esta organização ainda não cadastrou nenhuma empresa cliente (unidade).",
                  )}
                </p>
              </div>
            )}
          </div>
        </Modal>

        <Modal
          open={showCreditCostsModal}
          onClose={() => {
            setShowCreditCostsModal(false);
            setCreditCostScope("global");
            setCreditOverrideOrgId("");
            setCreditOverrideClientId("");
            setNewOverrideForm({ usage_type: "", cost_credits: "" });
          }}
          title={t("Tabela de Créditos")}
          subtitle={t(
            "Preço em créditos por tipo de uso — global, ou com exceção por organização/Empresa Cliente (prioridade: Cliente > Organização > Global).",
          )}
          maxWidth="lg"
        >
          <div className="space-y-4">
            <div className="flex gap-2 border-b border-gray-100 pb-3">
              {(["global", "organization", "client"] as const).map((scope) => (
                <button
                  key={scope}
                  onClick={() => {
                    setCreditCostScope(scope);
                    setCreditOverrideClientId("");
                  }}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                    creditCostScope === scope
                      ? "bg-emerald-600 text-white"
                      : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                  }`}
                >
                  {scope === "global"
                    ? t("Global")
                    : scope === "organization"
                      ? t("Por Organização")
                      : t("Por Empresa Cliente")}
                </button>
              ))}
            </div>

            {creditCostScope === "global" ? (
              <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1">
                {loadingCreditCosts ? (
                  <p className="text-sm text-gray-400 text-center py-6">
                    {t("Carregando...")}
                  </p>
                ) : (
                  creditCosts.map((c) => (
                    <div
                      key={c.usage_type}
                      className="flex items-center gap-2 p-2.5 rounded-xl bg-gray-50 border border-gray-100"
                    >
                      <span className="flex-1 text-xs font-bold text-gray-700 truncate">
                        {c.usage_type}
                      </span>
                      <input
                        type="number"
                        min={0}
                        value={editingCreditCosts[c.usage_type] ?? ""}
                        onChange={(e) =>
                          setEditingCreditCosts((prev) => ({
                            ...prev,
                            [c.usage_type]: e.target.value,
                          }))
                        }
                        className="w-20 h-8 px-2 rounded-lg border border-gray-200 text-sm font-bold text-center"
                      />
                      <Button
                        type="button"
                        size="sm"
                        isLoading={savingCreditCostUsageType === c.usage_type}
                        disabled={
                          String(c.cost_credits) ===
                          editingCreditCosts[c.usage_type]
                        }
                        onClick={() => handleSaveCreditCost(c.usage_type)}
                        className="h-8 min-h-[32px]! px-3 text-[10px] font-black uppercase tracking-widest"
                      >
                        {t("Salvar")}
                      </Button>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Select
                    label={t("Organização")}
                    value={creditOverrideOrgId}
                    onChange={(v) => {
                      setCreditOverrideOrgId(v as string);
                      setCreditOverrideClientId("");
                    }}
                    options={[
                      { value: "", label: t("Selecione...") },
                      ...companies.map((c) => ({
                        value: String(c.id),
                        label: c.name,
                      })),
                    ]}
                  />
                  {creditCostScope === "client" && (
                    <Select
                      label={t("Empresa Cliente")}
                      value={creditOverrideClientId}
                      onChange={(v) => setCreditOverrideClientId(v as string)}
                      disabled={!creditOverrideOrgId}
                      options={[
                        { value: "", label: t("Selecione...") },
                        ...(companies
                          .find((c) => String(c.id) === creditOverrideOrgId)
                          ?.client_companies.map((cc) => ({
                            value: String(cc.id),
                            label: cc.name,
                          })) || []),
                      ]}
                    />
                  )}
                </div>

                {(creditCostScope === "organization"
                  ? creditOverrideOrgId
                  : creditOverrideClientId) && (
                  <>
                    <div className="max-h-[30vh] overflow-y-auto space-y-2 pr-1">
                      {loadingCreditOverrides ? (
                        <p className="text-sm text-gray-400 text-center py-4">
                          {t("Carregando...")}
                        </p>
                      ) : creditOverrides.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-4">
                          {t(
                            "Nenhum override configurado — usa o preço global.",
                          )}
                        </p>
                      ) : (
                        creditOverrides.map((o) => (
                          <div
                            key={o.usage_type}
                            className="flex items-center gap-2 p-2.5 rounded-xl bg-emerald-50 border border-emerald-100"
                          >
                            <span className="flex-1 text-xs font-bold text-gray-700 truncate">
                              {o.usage_type}
                            </span>
                            <span className="text-sm font-black text-emerald-700 w-12 text-center">
                              {o.cost_credits}
                            </span>
                            <button
                              onClick={() => handleDeleteOverride(o.usage_type)}
                              disabled={
                                deletingOverrideUsageType === o.usage_type
                              }
                              className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-100 transition-colors disabled:opacity-50"
                              title={t("Remover override")}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="flex items-end gap-2 pt-3 border-t border-gray-100">
                      <Select
                        label={t("Tipo de uso")}
                        value={newOverrideForm.usage_type}
                        onChange={(v) =>
                          setNewOverrideForm((p) => ({
                            ...p,
                            usage_type: v as string,
                          }))
                        }
                        options={[
                          { value: "", label: t("Selecione...") },
                          ...creditCosts.map((c) => ({
                            value: c.usage_type,
                            label: c.usage_type,
                          })),
                        ]}
                        className="flex-1"
                      />
                      <Input
                        label={t("Créditos")}
                        type="number"
                        min="0"
                        value={newOverrideForm.cost_credits}
                        onChange={(e) =>
                          setNewOverrideForm((p) => ({
                            ...p,
                            cost_credits: e.target.value,
                          }))
                        }
                        className="w-24"
                      />
                      <Button
                        type="button"
                        onClick={handleAddOverride}
                        isLoading={savingOverride}
                        className="h-10 px-4 text-[10px] font-black uppercase tracking-widest shrink-0"
                      >
                        {t("Adicionar")}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </Modal>
      </div>
    </PageLayout>
  );
};

export default AdminCompanies;
